import {
	AfterViewInit, ChangeDetectorRef, Component, ElementRef,
	forwardRef, Inject, Input, NgZone, OnDestroy, Optional, ViewChild
} from '@angular/core';
import { AuditTrailAuditEntry }                          from '@cs/components/audit-trail';
import { BottomBarService }                              from '@cs/components/cs-bottom-button-bar';
import { DynamicButtonRegistration }                     from '@cs/components/cs-dynamic-button-bar';
import { DashboardPanelComponentBase, IButton }          from '@cs/components/shared';
import { FormLayout, Result }                            from '@cs/core/generate';
import { getPropertyOf }                                 from '@cs/core/utils';
import { NotifyServerForChangesDashboardPanelEventArgs } from '../../models/notify-server-for-changes-dashboard-panel-event-args';
import {
	ArrayUtils,
	CsHttpRequestOptions,
	DataDescribed, gv,
	LayoutAnnotation,
	PropertyAnnotation,
	updateTargetSources,
	ValidationResult
}                                                        from '@cs/core';
import { DashboardEventHub }                             from '../../dashboard-event-hub.service';
import { FormGeneratorNxtComponent }                     from '@cs/components/form-generator-nxt';
import { HttpErrorResponse }                             from '@angular/common/http';
import { BehaviorSubject, Observable, Subject, Subscription } from 'rxjs';
import { CsToastManagerService }                              from '@cs/components/toast-manager';
import { FilterCompareBarQuery }                              from '@cs/components/filter-and-compare-bar';
import { TranslateService }                                   from '@ngx-translate/core';
import { map, take }                                          from 'rxjs/operators';
import { DynamicButtonBarClickedEventArgs }                   from '@cs/components/cs-dynamic-button-bar';
import { isArray }                                       from '@cs/core';
import { MatDialogRef }                                  from '@angular/material/dialog';


@Component({
												selector:    'cs-dashboard-form-generator',
												templateUrl: './dashboard-form-generator.component.html',
												styleUrls:   ['./dashboard-form-generator.component.scss'],
												standalone: false
											})
export class DashboardFormGeneratorComponent extends DashboardPanelComponentBase<DataDescribed<any>> implements OnDestroy, AfterViewInit {


	@ViewChild(FormGeneratorNxtComponent, {static: true}) formGenerator: FormGeneratorNxtComponent<DataDescribed<any>>;
	@ViewChild('saveBtn', {static: false, read: ElementRef}) saveButton: ElementRef;

	name: string;
	requestInProgress$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
	forceDisable                                 = false;
	isDirty                                      = false;
	private formStatusSubscription: Subscription;
	private formDirtySubscription: Subscription;
	private buttonClickedSubscription: Subscription;
	contextObject: {
		[p: string]: any
	};

	/**
		* The data source that contains the values of the statistics panel.
		*/
	dynamicButtons: Array<IButton> = [{
		label:       'collapse',
		name:        'collapse',
		iconClass:   'mdi mdi-arrow-collapse mr-1',
		description: '',
		type:        'default'
	}];

	public get data(): DataDescribed<unknown> {
		return this._data;
	}

	public set data(value: DataDescribed<unknown>) {
		if (!(value instanceof DataDescribed)) {
			value = new DataDescribed(value);
		}


		this._data = value;

		this.bottomBarService.inputRequired(((this._data.layout as any).form as FormLayout<any>).layout?.mandatoryCommentOnChange ?? false);
		this.bottomBarService.inputOptions(((this._data.layout as any).form as FormLayout<any>).layout?.useReasonOnSave ?? null);

	}

	constructor(@Inject(forwardRef(() => DashboardEventHub)) private dashboardEventHub: DashboardEventHub,
													@Optional() @Inject(MatDialogRef) private matReg: MatDialogRef<any>,
													private changeRef: ChangeDetectorRef,
													private toastManageer: CsToastManagerService,
													private i8n: TranslateService,
													private ngZone: NgZone,
													@Optional() private filterCompareBarQuery: FilterCompareBarQuery,
													@Optional() private bottomBarService: BottomBarService
	) {
		super();
		this.contextObject = dashboardEventHub.getParentContext();
		// Wire Save/Cancel button clicks — done in constructor so it's always active,
		// regardless of whether update() is ever called (data is often set directly on the instance).
		this.setupButtonClickedListener();
	}

	ngAfterViewInit(): void {
		// formGenerator is available here; set up form-validity monitoring for the Save button state.
		this.setupFormValidityMonitoring();
	}

	update(data: any): void {
		if (this.data && ArrayUtils.isEqual(this.data, data))
			return;

		this.data = data;
		this.setupFormValidityMonitoring();

	}

	dataChanged(value: DataDescribed<any, LayoutAnnotation<any>, any, any>) {

	}

	isValid(): boolean {
		return this.formGenerator?.form?.formGroup?.valid ?? true;
	}

	save(auditEntry: AuditTrailAuditEntry): Observable<Result<boolean>> {
		return this.notifyChangesToServer(auditEntry);
	}

	cancel(): Observable<Result<boolean, null>> {
		this.cancelChanges();
		this.matReg.close();
		return new Observable(observer => {
			observer.next(Result.success(true));
			observer.complete();
		});
	}


	notifyChangesToServer(auditEntry: AuditTrailAuditEntry): Observable<Result<boolean>> {
		const subject = new Subject<Result<boolean>>();

		this.requestInProgress$.next(true);

		const options                = new CsHttpRequestOptions();
		options.errorResponseHandler = (response: HttpErrorResponse) => {
			this.requestInProgress$.next(false);
			subject.error(false);
			subject.complete();

			switch (response.status) {
				case 400:
					if (isArray(response)) {
						this.formGenerator.showErrorResponse(response.error.map(e => new ValidationResult(e)));
						this.changeRef.detectChanges();
						return true;
					}
					return false;
				default:
					return true;
			}

		};

		const data      = this.formGenerator.getFormData(false,
																																																			true,
																																																			gv(
																																																				() => ((this.data.layout as any).form as FormLayout<any>).layout.excludeDisabled, false)
		);
		const dashboard = this.dashboardEventHub.getParent();

		const selectionObject = {
			...this.data.getSelectionObject(),
			auditEntry: auditEntry,
			dashboard:  dashboard.data.name, // Deprecated
			...gv(() => this.filterCompareBarQuery.getValue().mainbarResultParams, {} as any)
		};

		this.dashboardEventHub
						.notifyChangesToServer(
							new NotifyServerForChangesDashboardPanelEventArgs<any>(
								{
									data,
									panelName:            this.name,
									selection:            selectionObject,
									csHttpRequestOptions: options,
									requestInProgress:    this.requestInProgress$,
									callback:             value => {
										this.toastManageer.show({
																																			type:    'success',
																																			content: this.i8n.instant('CHANGES_ARE_SAVED')
																																		});

										const result = value.value;

										if (isArray(result) && result.length > 0) {
											const message = result.reduce((prev: string[], current, index: number) => {
												const r = new ValidationResult(current);
												if (r.type === 'warning') {
													prev.push(r.errorMessage);
												}
												return prev;
											}, []);
											this.toastManageer.show({
																																				type:         'warning',
																																				content:      message.join('\n'),
																																				clickToClose: true
																																			});
										}

										this.formGenerator.setPristineAgain();
										this.isDirty = false;
										this.ngZone.onStable.pipe(take(1))
														.subscribe(() => {
															setTimeout(() => {
																//this.saveButton.nativeElement.setAttribute('disabled', `${true}`);

																if (true && this.matReg) {
																	this.matReg.close();
																}
															}, 100);
														});

										subject.next(Result.success(true));
										subject.complete();
										this.changeRef.detectChanges();
									}
								}));

		return subject.asObservable();
	}

	cancelChanges() {
		this.formGenerator.resetFormData();
		this.isDirty = false;
	}


	valueChanged($event: unknown) {
		this.isDirty = true;
		// this.formGenerator.showCurrentErrorStatuses();
	}

	/**
		* Show the state of the form. When not valid messages should appear below the fields
		* @param $event
		*/
	showFormState($event: MouseEvent) {
		const f = this.formGenerator.form.formGroup;
		if (f.pristine || f.valid) {
			return;
		}

		this.formGenerator.showCurrentErrorStatuses();
	}

	actionRequestedHandler($event: PropertyAnnotation<any>) {
		const result = updateTargetSources({row: this.formGenerator.getFormData(), column: $event}, this.data, this.name, false);
		this.dashboardEventHub.triggerDashboardEntryClicked(result);
	}

	private setupFormValidityMonitoring() {
		// Unsubscribe from previous subscription if it exists
		if (this.formStatusSubscription) {
			this.formStatusSubscription.unsubscribe();
		}
		if (this.formDirtySubscription) {
			this.formDirtySubscription.unsubscribe();
		}

		// Subscribe to form status changes and update the bottom bar service
		if (this.formGenerator?.form?.formGroup) {
			this.formStatusSubscription = this.formGenerator.form.formGroup.statusChanges
				.subscribe(() => {
					this.bottomBarService?.isDataValid(this.formGenerator.form.formGroup.valid);
				});

			// valueChanges fires immediately on every user change.
			// Checking formGroup.dirty guards against programmatic setValue/patchValue
			// calls during form initialization, which do NOT mark the form dirty.
			this.formDirtySubscription = this.formGenerator.form.formGroup.valueChanges
				.subscribe(() => {
					if (this.formGenerator.form.formGroup.dirty) {
						this.isDirty = true;
						this.changeRef.detectChanges();
					}
				});

			this.bottomBarService?.isDataValid(this.formGenerator.form.formGroup.valid);
		}
	}

	ngOnDestroy() {
		if (this.formStatusSubscription) {
			this.formStatusSubscription.unsubscribe();
		}
		if (this.formDirtySubscription) {
			this.formDirtySubscription.unsubscribe();
		}
		if (this.buttonClickedSubscription) {
			this.buttonClickedSubscription.unsubscribe();
		}
	}

	private setupButtonClickedListener() {
		if (!this.bottomBarService)
			return;

		if (this.buttonClickedSubscription) {
			this.buttonClickedSubscription.unsubscribe();
		}

		this.buttonClickedSubscription = this.bottomBarService.onButtonClicked
			.subscribe((clickedEvent: DynamicButtonBarClickedEventArgs) => {
				const buttonId = clickedEvent?.data?.buttonId;
				if (buttonId === 'Save') {
					this.notifyChangesToServer(null);
				} else if (buttonId === 'Cancel') {
					this.cancelChanges();
				}
			});
	}
}
